// ===========================================
// Events API Functions
// ===========================================
// API calls for Events management (Phase 5 — Priority 1)

import { apiClient } from '../services/api';

// ===========================================
// ENUMS (string-literal unions matching Prisma enums)
// ===========================================

// NOTE: these MUST match the backend Prisma enums (EventType, EventVisibility)
// exactly — the backend validates with z.nativeEnum, so any drift causes a 400.
export type EventType =
  | 'CONCERT'
  | 'RECITAL'
  | 'WORKSHOP'
  | 'OPEN_HOUSE'
  | 'MASTERCLASS'
  | 'REHEARSAL'
  | 'RECORDING_SESSION'
  | 'COMMUNITY_SERVICE'
  | 'PARENT_NIGHT'
  | 'HOLIDAY_CAMP'
  | 'OTHER';

export type EventVisibility =
  | 'ALL_FAMILIES'
  | 'SPECIFIC_FAMILIES'
  | 'SPECIFIC_CLASSES'
  | 'SPECIFIC_AGE_GROUPS'
  | 'SPECIFIC_PROGRAM_LEVELS'
  | 'STAFF_ONLY'
  | 'PUBLIC';

export type EventStatus =
  | 'DRAFT'
  | 'PUBLISHED'
  | 'CANCELLED'
  | 'COMPLETED'
  | 'ARCHIVED';

export type RSVPResponse =
  | 'ATTENDING'
  | 'NOT_ATTENDING'
  | 'MAYBE'
  | 'PENDING'
  | 'WAITLIST';

// Mirrors the Prisma TicketStatus enum exactly. (Legacy 'CONFIRMED'/'USED'
// never existed in the schema — they left every PAID ticket on a grey badge.)
export type TicketStatus =
  | 'PENDING'
  | 'PAID'
  | 'CANCELLED'
  | 'REFUNDED'
  | 'CHECKED_IN';

export type EventPhotoSource =
  | 'UPLOAD'
  | 'PIXABAY';

export type AttendanceStatus =
  | 'PRESENT'
  | 'ABSENT'
  | 'LATE'
  | 'EXCUSED';

// ===========================================
// SHARED RELATION SHAPES
// ===========================================

export interface UserSummary {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

export interface LocationSummary {
  id: string;
  name: string;
  // Broadened (venue Phase 2/5): the backend EVENT_*_INCLUDE now selects the
  // location's structured address so the detail pages can show/format it.
  streetAddress?: string | null;
  unit?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  // Geocoded coordinates (for the venue map on event detail pages).
  latitude?: number | null;
  longitude?: number | null;
}

export interface RoomSummary {
  id: string;
  name: string;
}

// ===========================================
// EVENT VENUE SHAPES (reusable external venues)
// ===========================================

// Coords/address summary attached to an event (EVENT_VENUE_SELECT on the backend).
export interface VenueSummary {
  id: string;
  name: string;
  streetAddress: string | null;
  unit: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string;
  phone: string | null;
  capacity: number | null;
  latitude: number | null;
  longitude: number | null;
}

// Full reusable venue (the /admin/events/venues CRUD shape).
export interface EventVenue extends VenueSummary {
  schoolId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEventVenueData {
  name: string;
  streetAddress?: string | null;
  unit?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  phone?: string | null;
  capacity?: number | null;
}

export type UpdateEventVenueData = Partial<CreateEventVenueData> & {
  /** Manual pin override (send both together); set directly, skips geocoding. */
  latitude?: number | null;
  longitude?: number | null;
  /** Recompute the pin from the stored address ("Reset to geocoded"). */
  regeocode?: boolean;
};

// ===========================================
// CORE EVENT SHAPES
// ===========================================

export interface EventListItem {
  id: string;
  schoolId: string;
  type: EventType;
  status: EventStatus;
  visibility: EventVisibility;
  title: string;
  slug: string;
  startDateTime: string;
  endDateTime: string;
  venueName: string | null;
  capacity: number | null;
  isPublic: boolean;
  isFree: boolean;
  /** @deprecated superseded by EventTicketTier[]. */
  ticketPriceCents: number | null;
  featuredImage: string | null;
  createdAt: string;
  updatedAt: string;
  // Offered ticket tiers (ordered by sortOrder) — lets list cards show "from $X".
  EventTicketTier?: EventTicketTierSummary[];
  _count?: {
    EventRSVP: number;
    EventRegistration: number;
    EventTicket: number;
  };
}

export interface Event {
  id: string;
  schoolId: string;
  type: EventType;
  status: EventStatus;
  visibility: EventVisibility;
  title: string;
  slug: string;
  description: string | null;
  startDateTime: string;
  endDateTime: string;
  locationId: string | null;
  roomId: string | null;
  venueId: string | null;
  venueName: string | null;
  capacity: number | null;
  registrationDeadline: string | null;
  isPublic: boolean;
  isFree: boolean;
  /** @deprecated superseded by EventTicketTier[]. */
  ticketPriceCents: number | null;
  featuredImage: string | null;
  requiresApproval: boolean;
  cancelledReason: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  /** ISO timestamp for a scheduled Save the Date announcement on publish (null = announce immediately). */
  announcementScheduledFor?: string | null;
  /** ISO timestamp when the automatic Save the Date announcement was last sent (null = not yet sent). */
  announcementSentAt?: string | null;
  targetFamilies?: string[];
  targetClasses?: string[];
  targetAgeGroups?: string[];
  targetProgramLevels?: string[];
  Location: LocationSummary | null;
  Room: RoomSummary | null;
  Venue: VenueSummary | null;
  CreatedBy: UserSummary;
  EventTicketTier?: EventTicketTierSummary[];
  _count?: {
    EventRSVP: number;
    EventRegistration: number;
    EventTicket: number;
    EventAttendance: number;
    EventPhoto: number;
  };
}

// ===========================================
// BUYER-FACING SHAPES (public site + parent portal) — #682
// ===========================================
// The backend sends an EXPLICIT field set on these four reads
// (EVENT_PUBLIC_*/EVENT_PARENT_* selects in services/event.service.ts). They are
// NOT `Event`/`EventListItem`, which are the ADMIN shapes: a field missing here is a
// field the server does not send, so reading it must not compile. Keep in step with
// those selects.

/** Location/venue as a buyer sees it: address + coords, no phone, no capacity. */
export interface BuyerPlaceSummary {
  id: string;
  name: string;
  streetAddress: string | null;
  unit: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string;
  latitude: number | null;
  longitude: number | null;
}

/** The card both lists show. */
interface BuyerEventCard {
  id: string;
  type: EventType;
  title: string;
  featuredImage: string | null;
  isFree: boolean;
  /** @deprecated superseded by EventTicketTier[]. */
  ticketPriceCents: number | null;
  startDateTime: string;
  endDateTime: string;
  venueName: string | null;
  EventTicketTier: EventTicketTierSummary[];
}

export interface PublicEventListItem extends BuyerEventCard {
  slug: string;
}

export interface PublicEventDetail extends PublicEventListItem {
  description: string | null;
  capacity: number | null;
  registrationDeadline: string | null;
  isPublic: boolean;
  Location: BuyerPlaceSummary | null;
  Room: RoomSummary | null;
  Venue: BuyerPlaceSummary | null;
}

/** The parent list carries no `slug`: the portal links by id. */
export type ParentEventListItem = BuyerEventCard;

export interface ParentEventDetail extends BuyerEventCard {
  status: EventStatus;
  description: string | null;
  capacity: number | null;
  registrationDeadline: string | null;
  Location: BuyerPlaceSummary | null;
  Room: RoomSummary | null;
  Venue: BuyerPlaceSummary | null;
  _count: { EventRSVP: number };
}

export interface PaginatedBuyerEvents<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface EventStats {
  draft: number;
  published: number;
  cancelled: number;
  completed: number;
  archived: number;
  total: number;
  upcoming: number;
}

// ===========================================
// RSVP SHAPES
// ===========================================

export interface EventRSVP {
  id: string;
  eventId: string;
  schoolId: string;
  familyId: string | null;
  studentId: string | null;
  response: RSVPResponse;
  guestCount: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RSVPWithRelations extends EventRSVP {
  Family: { id: string; name: string } | null;
  Student: { id: string; firstName: string; lastName: string } | null;
}

export interface RSVPStats {
  attending: number;
  notAttending: number;
  maybe: number;
  pending: number;
  waitlist: number;
  total: number;
  totalGuests: number;
}

// ===========================================
// REGISTRATION SHAPES
// ===========================================

export interface EventRegistration {
  id: string;
  eventId: string;
  schoolId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  guestCount: number;
  status: RSVPResponse;
  convertedToFamilyId: string | null;
  ipAddress: string | null;
  createdAt: string;
  updatedAt: string;
}

// ===========================================
// TICKET SHAPES
// ===========================================

export interface EventTicket {
  id: string;
  eventId: string;
  schoolId: string;
  holderName: string;
  holderEmail: string;
  // One row per ticket. Each carries its tier + the price it was bought at.
  tierId: string | null;
  priceCents: number;
  status: TicketStatus;
  stripeSessionId: string | null;
  stripePaymentIntentId: string | null;
  rsvpId: string | null;
  registrationId: string | null;
  qrToken: string;
  checkedInAt: string | null;
  checkedInById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TicketCheckoutResult {
  sessionId: string;
  url: string;
}

// A priced ticket tier offered by an event (see backend EventTicketTier).
export interface EventTicketTierSummary {
  id: string;
  key: string;
  label: string;
  priceCents: number;
  sortOrder: number;
  /**
   * Last day this tier sells, INCLUSIVE — a bare `YYYY-MM-DD` calendar day
   * (`@db.Date`), never an instant. Null = no expiry. Rebuild it with
   * `localDateFromKey` before any `format()`, and send it back with
   * `toLocalDateString`; `.toISOString()` shifts the day west of UTC.
   */
  availableUntil?: string | null;
}

// One line of a mixed-tier order: how many of a given tier to buy.
export interface TicketItemInput {
  tierId: string;
  quantity: number;
}

// ===========================================
// ATTENDANCE SHAPES
// ===========================================

export interface EventAttendance {
  id: string;
  eventId: string;
  schoolId: string;
  studentId: string | null;
  registrationId: string | null;
  ticketId: string | null;
  status: AttendanceStatus;
  notes: string | null;
  markedById: string;
  createdAt: string;
  updatedAt: string;
  // Included relations (present depending on which key the row is linked by).
  Student?: { id: string; firstName: string; lastName: string } | null;
  EventTicket?: { id: string; holderName: string } | null;
}

export interface CheckInResult {
  ticketId: string;
  holderName: string;
  eventTitle: string;
  alreadyCheckedIn: boolean;
  checkedInAt: string;
}

// ===========================================
// PHOTO SHAPES
// ===========================================

export interface EventPhoto {
  id: string;
  eventId: string;
  schoolId: string;
  source: EventPhotoSource;
  imageUrl: string;
  caption: string | null;
  pixabayPageUrl: string | null;
  pixabayUser: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface PixabaySearchResult {
  pixabayId: number;
  previewUrl: string;
  fullUrl: string;
  pageUrl: string;
  user: string;
  tags: string;
}

// ===========================================
// INPUT / FILTER TYPES
// ===========================================

export interface CreateEventData {
  type: EventType;
  visibility?: EventVisibility;
  title: string;
  description?: string;
  startDateTime: string;
  endDateTime: string;
  locationId?: string | null;
  roomId?: string | null;
  venueId?: string | null;
  venueName?: string | null;
  capacity?: number | null;
  registrationDeadline?: string | null;
  isPublic?: boolean;
  isFree?: boolean;
  /** @deprecated superseded by `tiers`. */
  ticketPriceCents?: number | null;
  // Offered ticket tiers. Omit/empty for a free event. Each entry activates a
  // standard tier (by key) at the given price.
  tiers?: Array<{ key: string; priceCents: number; availableUntil?: string | null }>;
  featuredImage?: string | null;
  requiresApproval?: boolean;
  targetFamilies?: string[];
  targetClasses?: string[];
  targetAgeGroups?: string[];
  targetProgramLevels?: string[];
  /** ISO timestamp to schedule an automatic Save the Date announcement on publish. Leave null to announce immediately. */
  announcementScheduledFor?: string | null;
}

export type UpdateEventData = Partial<CreateEventData>;

export interface EventFilters {
  status?: EventStatus;
  type?: EventType;
  visibility?: EventVisibility;
  isPublic?: boolean;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  upcomingOnly?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedEventResult {
  data: EventListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface EventSubListFilters {
  response?: RSVPResponse;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedRSVPResult {
  data: RSVPWithRelations[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedRegistrationResult {
  data: EventRegistration[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface MarkAttendanceItem {
  studentId?: string | null;
  registrationId?: string | null;
  status: AttendanceStatus;
  notes?: string;
}

export interface AddPixabayPhotoData {
  pixabayPageUrl: string;
  imageUrl: string;
  user: string;
  caption?: string;
}

// The family's own answer, as GET /parents/events/:id/rsvp/mine sends it (#253)
export interface MyRsvp {
  id: string;
  response: RSVPResponse;
  guestCount: number;
  notes: string | null;
  updatedAt: string;
}

// Parent RSVP input — no familyId (server derives from session)
export interface ParentRsvpData {
  response: RSVPResponse;
  studentId?: string | null;
  guestCount?: number;
  notes?: string;
}

// Parent ticket checkout input
export interface ParentTicketCheckoutData {
  holderName: string;
  holderEmail: string;
  // Per-tier quantities (mixed-cart). Preferred over `quantity`.
  items?: TicketItemInput[];
  /** @deprecated legacy single-price fallback; superseded by `items`. */
  quantity?: number;
  rsvpId?: string | null;
  successUrl: string;
  cancelUrl: string;
}

// A parent's owned ticket for an event (returned by GET /parents/events/:id/tickets/mine).
// This is a safe subset — Stripe internal IDs are never included.
export interface MyEventTicket {
  id: string;
  status: 'PAID' | 'CHECKED_IN';
  tierId: string | null;
  priceCents: number;
  holderName: string;
  qrToken: string;
  checkedInAt: string | null;
  createdAt: string;
  EventTicketTier: { key: string; label: string } | null;
}

// Public registration input
export interface PublicEventRegistrationData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  guestCount?: number;
  website?: string; // honeypot
}

// Public ticket checkout input (no rsvpId — guest path)
export interface PublicTicketCheckoutData {
  holderName: string;
  holderEmail: string;
  // Per-tier quantities (mixed-cart). Preferred over `quantity`.
  items?: TicketItemInput[];
  /** @deprecated legacy single-price fallback; superseded by `items`. */
  quantity?: number;
  successUrl: string;
  cancelUrl: string;
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// ADMIN API (Auth required — /admin/events)
// ===========================================

export const eventApi = {
  /**
   * GET /admin/events
   * List events with filters + pagination
   */
  list: (filters?: EventFilters) =>
    apiClient
      .get<ApiResponse<PaginatedEventResult>>('/admin/events', { params: filters })
      .then((res) => res.data),

  /**
   * GET /admin/events/stats
   * Get event counts by status
   */
  getStats: () =>
    apiClient
      .get<ApiResponse<EventStats>>('/admin/events/stats')
      .then((res) => res.data),

  /**
   * GET /admin/events/:id
   * Get a single event with full details
   */
  get: (id: string) =>
    apiClient
      .get<ApiResponse<Event>>(`/admin/events/${id}`)
      .then((res) => res.data),

  /**
   * POST /admin/events
   * Create a new event (starts as DRAFT)
   */
  create: (data: CreateEventData) =>
    apiClient
      .post<ApiResponse<Event>>('/admin/events', data)
      .then((res) => res.data),

  /**
   * PATCH /admin/events/:id
   * Update event fields
   */
  update: (id: string, data: UpdateEventData) =>
    apiClient
      .patch<ApiResponse<Event>>(`/admin/events/${id}`, data)
      .then((res) => res.data),

  /**
   * POST /admin/events/:id/publish
   * Publish a draft event
   */
  publish: (id: string) =>
    apiClient
      .post<ApiResponse<Event>>(`/admin/events/${id}/publish`)
      .then((res) => res.data),

  /**
   * POST /admin/events/:id/cancel
   * Cancel a published event with optional reason
   */
  cancel: (id: string, reason?: string) =>
    apiClient
      .post<ApiResponse<Event>>(`/admin/events/${id}/cancel`, { reason })
      .then((res) => res.data),

  /**
   * POST /admin/events/:id/complete
   * Mark a published event as completed
   */
  complete: (id: string) =>
    apiClient
      .post<ApiResponse<Event>>(`/admin/events/${id}/complete`)
      .then((res) => res.data),

  /**
   * DELETE /admin/events/:id
   * Delete an event (DRAFT or CANCELLED only)
   */
  delete: (id: string) =>
    apiClient
      .delete<ApiResponse<{ message: string }>>(`/admin/events/${id}`)
      .then((res) => res.data),

  // --- RSVPs ---

  /**
   * GET /admin/events/:id/rsvps
   * List RSVPs for an event
   */
  listRsvps: (eventId: string, filters?: EventSubListFilters) =>
    apiClient
      .get<ApiResponse<PaginatedRSVPResult>>(`/admin/events/${eventId}/rsvps`, {
        params: filters,
      })
      .then((res) => res.data),

  /**
   * GET /admin/events/:id/rsvps/stats
   * Get RSVP statistics for an event
   */
  getRsvpStats: (eventId: string) =>
    apiClient
      .get<ApiResponse<RSVPStats>>(`/admin/events/${eventId}/rsvps/stats`)
      .then((res) => res.data),

  /**
   * PATCH /admin/events/:id/rsvps/:rsvpId
   * Update the response on a specific RSVP
   */
  updateRsvpStatus: (eventId: string, rsvpId: string, response: RSVPResponse) =>
    apiClient
      .patch<ApiResponse<RSVPWithRelations>>(
        `/admin/events/${eventId}/rsvps/${rsvpId}`,
        { response }
      )
      .then((res) => res.data),

  // --- Registrations ---

  /**
   * GET /admin/events/:id/registrations
   * List public registrations for an event
   */
  listRegistrations: (eventId: string, filters?: EventSubListFilters) =>
    apiClient
      .get<ApiResponse<PaginatedRegistrationResult>>(
        `/admin/events/${eventId}/registrations`,
        { params: filters }
      )
      .then((res) => res.data),

  /**
   * POST /admin/events/:id/registrations/:rid/convert
   * Convert a public registration into a family account
   */
  convertRegistration: (eventId: string, registrationId: string) =>
    apiClient
      .post<ApiResponse<{ message: string }>>(
        `/admin/events/${eventId}/registrations/${registrationId}/convert`
      )
      .then((res) => res.data),

  // --- Attendance ---

  /**
   * POST /admin/events/:id/attendance
   * Bulk-mark attendance for an event
   */
  markAttendance: (eventId: string, attendances: MarkAttendanceItem[]) =>
    apiClient
      .post<ApiResponse<EventAttendance[]>>(`/admin/events/${eventId}/attendance`, {
        attendances,
      })
      .then((res) => res.data),

  /**
   * GET /admin/events/:id/attendance
   * List attendance records for an event
   */
  listAttendance: (eventId: string) =>
    apiClient
      .get<ApiResponse<EventAttendance[]>>(`/admin/events/${eventId}/attendance`)
      .then((res) => res.data),

  /**
   * POST /admin/events/:id/checkin
   * Check in a ticket holder by QR token
   */
  checkIn: (eventId: string, qrToken: string) =>
    apiClient
      .post<ApiResponse<CheckInResult>>(`/admin/events/${eventId}/checkin`, { qrToken })
      .then((res) => res.data),

  // --- Tickets ---

  /**
   * GET /admin/events/:id/tickets
   * List tickets for an event (optional status filter)
   */
  listTickets: (eventId: string, status?: TicketStatus) =>
    apiClient
      .get<ApiResponse<EventTicket[]>>(`/admin/events/${eventId}/tickets`, {
        params: status ? { status } : undefined,
      })
      .then((res) => res.data),

  // --- Photos ---

  /**
   * GET /admin/events/:id/photos
   * List photos for an event
   */
  listPhotos: (eventId: string) =>
    apiClient
      .get<ApiResponse<EventPhoto[]>>(`/admin/events/${eventId}/photos`)
      .then((res) => res.data),

  /**
   * GET /admin/events/photos/pixabay/search
   * Search Pixabay for event photos
   */
  searchPixabay: (q: string, page?: number) =>
    apiClient
      .get<ApiResponse<PixabaySearchResult[]>>('/admin/events/photos/pixabay/search', {
        params: { q, page },
      })
      .then((res) => res.data),

  /**
   * POST /admin/events/feature-image
   * Upload a file for use as an event featured image (event-independent).
   * Returns { url } — no eventId required.
   */
  uploadFeatureImage: (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient
      .post<ApiResponse<{ url: string }>>('/admin/events/feature-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data);
  },

  /**
   * POST /admin/events/feature-image/pixabay
   * Download a Pixabay image into our storage for use as event featured image.
   * Returns { url } — no eventId required.
   */
  selectPixabayFeatureImage: (body: {
    imageUrl: string;
    pixabayPageUrl?: string;
    user?: string;
  }): Promise<{ url: string }> =>
    apiClient
      .post<ApiResponse<{ url: string }>>('/admin/events/feature-image/pixabay', body)
      .then((res) => res.data),

  /**
   * POST /admin/events/:id/photos/pixabay
   * Add a Pixabay photo to an event gallery
   */
  addPixabayPhoto: (eventId: string, data: AddPixabayPhotoData) =>
    apiClient
      .post<ApiResponse<EventPhoto>>(`/admin/events/${eventId}/photos/pixabay`, data)
      .then((res) => res.data),

  /**
   * DELETE /admin/events/:id/photos/:photoId
   * Delete a photo from an event gallery
   */
  deletePhoto: (eventId: string, photoId: string) =>
    apiClient
      .delete<ApiResponse<{ message: string }>>(
        `/admin/events/${eventId}/photos/${photoId}`
      )
      .then((res) => res.data),

  // --- Venues (reusable external venues) ---

  /**
   * GET /admin/events/venues
   * List all reusable venues for the school (alphabetical)
   */
  listVenues: () =>
    apiClient
      .get<ApiResponse<EventVenue[]>>('/admin/events/venues')
      .then((res) => res.data),

  /**
   * GET /admin/events/venues/:venueId
   * Get a single reusable venue
   */
  getVenue: (venueId: string) =>
    apiClient
      .get<ApiResponse<EventVenue>>(`/admin/events/venues/${venueId}`)
      .then((res) => res.data),

  /**
   * POST /admin/events/venues
   * Create a reusable venue (geocoded best-effort on save)
   */
  createVenue: (data: CreateEventVenueData) =>
    apiClient
      .post<ApiResponse<EventVenue>>('/admin/events/venues', data)
      .then((res) => res.data),

  /**
   * PATCH /admin/events/venues/:venueId
   * Update a reusable venue
   */
  updateVenue: (venueId: string, data: UpdateEventVenueData) =>
    apiClient
      .patch<ApiResponse<EventVenue>>(`/admin/events/venues/${venueId}`, data)
      .then((res) => res.data),

  /**
   * DELETE /admin/events/venues/:venueId
   * Delete a reusable venue (409 if any event references it)
   */
  deleteVenue: (venueId: string) =>
    apiClient
      .delete<ApiResponse<{ message: string }>>(`/admin/events/venues/${venueId}`)
      .then((res) => res.data),
};

// ===========================================
// PARENT API (Auth required — /parents/events)
// ===========================================

export const eventParentApi = {
  /**
   * GET /parents/events
   * List PUBLISHED upcoming events visible to authenticated parents
   */
  list: (params?: { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) =>
    apiClient
      .get<ApiResponse<PaginatedBuyerEvents<ParentEventListItem>>>('/parents/events', { params })
      .then((res) => res.data),

  /**
   * GET /parents/events/:id
   * Get a single PUBLISHED event
   */
  get: (id: string) =>
    apiClient
      .get<ApiResponse<ParentEventDetail>>(`/parents/events/${id}`)
      .then((res) => res.data),

  /**
   * GET /parents/events/:id/rsvp/mine
   * The family's own answer, or null when it has not answered (#253).
   */
  getMyRsvp: (eventId: string) =>
    apiClient
      .get<ApiResponse<MyRsvp | null>>(`/parents/events/${eventId}/rsvp/mine`)
      .then((res) => res.data),

  /**
   * POST /parents/events/:id/rsvp
   * Submit or update an RSVP for the authenticated parent's family.
   * familyId is derived server-side from the session — do NOT include it here.
   */
  rsvp: (eventId: string, data: ParentRsvpData) =>
    apiClient
      .post<ApiResponse<EventRSVP>>(`/parents/events/${eventId}/rsvp`, data)
      .then((res) => res.data),

  /**
   * POST /parents/events/:id/tickets/checkout
   * Initiate a Stripe Checkout session for ticket purchase.
   * Returns { sessionId, url } — caller should redirect to url.
   */
  checkout: (eventId: string, data: ParentTicketCheckoutData) =>
    apiClient
      .post<ApiResponse<TicketCheckoutResult>>(
        `/parents/events/${eventId}/tickets/checkout`,
        data
      )
      .then((res) => res.data),

  /**
   * GET /parents/events/tickets/:ticketId/pdf
   * Download a PDF ticket as a blob.
   */
  getTicketPdf: (ticketId: string) =>
    apiClient
      .get<BlobPart>(`/parents/events/tickets/${ticketId}/pdf`, {
        responseType: 'blob',
      })
      .then((blobPart) => new Blob([blobPart], { type: 'application/pdf' })),

  /**
   * GET /parents/events/:eventId/tickets/mine
   * Return the authenticated parent's own PAID/CHECKED_IN tickets for an event.
   *
   * Double-unwrap convention: apiClient strips the Axios wrapper (.data), then
   * the api method strips the { status, data } envelope (.then(res => res.data)),
   * so the hook receives the bare MyEventTicket[].
   */
  getMyEventTickets: (eventId: string) =>
    apiClient
      .get<ApiResponse<MyEventTicket[]>>(`/parents/events/${eventId}/tickets/mine`)
      .then((res) => res.data),
};

// ===========================================
// PUBLIC API (No auth — /public/schools/:slug/events)
// ===========================================

export const eventPublicApi = {
  /**
   * GET /public/schools/:slug/events
   * List PUBLISHED + isPublic upcoming events for a school
   */
  list: (slug: string, params?: { page?: number; limit?: number }) =>
    apiClient
      .get<ApiResponse<PaginatedBuyerEvents<PublicEventListItem>>>(`/public/schools/${slug}/events`, {
        params,
      })
      .then((res) => res.data),

  /**
   * GET /public/schools/:slug/events/:eventSlug
   * Public event detail — PUBLISHED + isPublic only
   */
  getBySlug: (slug: string, eventSlug: string) =>
    apiClient
      .get<ApiResponse<PublicEventDetail>>(`/public/schools/${slug}/events/${eventSlug}`)
      .then((res) => res.data),

  /**
   * POST /public/schools/:slug/events/:eventSlug/register
   * Anonymous public registration (lead-capture)
   */
  register: (slug: string, eventSlug: string, data: PublicEventRegistrationData) =>
    apiClient
      .post<ApiResponse<{ id: string; message: string }>>(
        `/public/schools/${slug}/events/${eventSlug}/register`,
        data
      )
      .then((res) => res.data),

  /**
   * POST /public/schools/:slug/events/:eventSlug/tickets/checkout
   * Guest Stripe Checkout session for a paid public event
   */
  checkout: (slug: string, eventSlug: string, data: PublicTicketCheckoutData) =>
    apiClient
      .post<ApiResponse<TicketCheckoutResult>>(
        `/public/schools/${slug}/events/${eventSlug}/tickets/checkout`,
        data
      )
      .then((res) => res.data),
};

// ===========================================
// ICS URL HELPERS (direct browser links)
// ===========================================

/**
 * URL for the subscribable iCalendar feed for all public events at a school.
 * Use as an <a href> or window.location for calendar subscribe flows.
 */
export function schoolFeedIcsUrl(slug: string): string {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
  return `${base}/public/schools/${slug}/events/feed.ics`;
}

/**
 * URL for a single-event iCalendar download for a public event.
 */
export function eventIcsUrl(slug: string, eventSlug: string): string {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
  return `${base}/public/schools/${slug}/events/${eventSlug}/calendar.ics`;
}

/**
 * ICS download URL keyed by event id (works for family-only events, not just
 * public ones — used by the logged-in parent event page). Backed by the
 * /events/by-id/:id/calendar.ics route.
 */
export function eventIcsByIdUrl(slug: string, eventId: string): string {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
  return `${base}/public/schools/${slug}/events/by-id/${eventId}/calendar.ics`;
}

/** Format a Date as a UTC iCal/Google timestamp: YYYYMMDDTHHMMSSZ. */
function toGoogleUtc(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}` +
    `T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`
  );
}

/**
 * "Add to Google Calendar" template URL — opens a pre-filled event the user
 * just clicks Save on (no auth). Mirrors the backend buildGoogleCalendarUrl.
 * start/end are ISO strings; end falls back to start + 1h when missing.
 */
export function googleCalendarEventUrl(opts: {
  title: string;
  start: string;
  end?: string | null;
  details?: string | null;
  location?: string | null;
}): string {
  const start = new Date(opts.start);
  const end = opts.end ? new Date(opts.end) : new Date(start.getTime() + 60 * 60 * 1000);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: opts.title,
    dates: `${toGoogleUtc(start)}/${toGoogleUtc(end)}`,
  });
  if (opts.details) params.set('details', opts.details);
  if (opts.location) params.set('location', opts.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
